import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useMutation } from "convex/react";
import { api } from "../../backend/_generated/api";
import { Id } from "../../backend/_generated/dataModel";

// Type definitions
type UserRole = "admin" | "investor" | "project_holder";
type UserType = "investor" | "project-holder" | "admin";
type UserStatus = "pending_approval" | "approved" | "rejected" | "suspended";

interface AuthUser {
  id: Id<"users">;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  phone?: string;
  company?: string;
  bio?: string;
  location?: string;
  investmentCapacity?: number;
  preferredSectors?: string[];
  // riskTolerance removed
  investmentExperience?: string;
  occupation?: string;
  linkedin?: string;
  projectName?: string;
  projectDescription?: string;
  fundingGoal?: number;
  sector?: string;
  teamSize?: number;
  fundingTimeline?: string;
  projectFiles?: string[];
  projectUrls?: string[];
  website?: string;
  projectPurpose?: "sell" | "getInvestment";
  assetsIncluded?: string[];
  financialOverview?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  userType: UserType | "admin" | null;
  isAdmin: boolean;
  login: (email: string, password: string, type: UserType | "admin") => Promise<boolean>;
  registerInvestor: (data: { name: string; email: string; password: string; phone?: string; company?: string; bio?: string; location?: string; investmentCapacity?: number; preferredSectors?: string[]; investmentExperience?: string; occupation?: string; linkedin?: string; }) => Promise<boolean>;
  registerProjectHolder: (data: { name: string; email: string; password: string; phone?: string; company?: string; bio?: string; location?: string; projectName?: string; projectDescription?: string; fundingGoal?: number; sector?: string; teamSize?: number; fundingTimeline?: string; projectFiles?: string[]; projectUrls?: string[]; website?: string; linkedin?: string; projectPurpose?: "sell" | "getInvestment"; assetsIncluded?: string[]; financialOverview?: string; }) => Promise<boolean>;
  registerAdmin: (data: { name: string; email: string; password: string; }) => Promise<boolean>;
  updateUser: (data: Partial<AuthUser>) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userType, setUserType] = useState<UserType | "admin" | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Convex mutations
  const loginMutation = useMutation(api.auth.login);
  const getUserMutation = useMutation(api.auth.getUser);
  const registerInvestorMutation = useMutation(api.auth.registerInvestor);
  const registerProjectHolderMutation = useMutation(api.auth.registerProjectHolder);
  const registerAdminMutation = useMutation(api.auth.registerAdmin);
  const updateUserMutation = useMutation(api.auth.updateUser);
  const changePasswordMutation = useMutation(api.auth.changePassword);

  useEffect(() => {
    // Check if user is already logged in (from localStorage or session)
    const storedUserId = localStorage.getItem("userId");
    const storedUserType = localStorage.getItem("userType");
    const storedIsAdmin = localStorage.getItem("isAdmin") === "true";

    if (storedUserId && storedUserType) {
      // Fetch user data from Convex for all users (including admin)
      getUserMutation({ userId: storedUserId as Id<"users"> })
        .then(userData => {
          if (userData) {
            // Check if user is admin
            const isAdminUser = userData.role === "admin";
            setIsAdmin(isAdminUser);

            setUser({
              id: userData.userId,
              name: userData.name,
              email: userData.email,
              role: userData.role,
              status: userData.status,
              phone: userData.phone,
              company: userData.company,
              bio: userData.bio,
              location: userData.location,
              investmentCapacity: userData.investmentCapacity,
              preferredSectors: userData.preferredSectors,
              // riskTolerance removed
              investmentExperience: userData.investmentExperience,
              occupation: userData.occupation,
              linkedin: userData.linkedin,
              projectName: userData.projectName,
              projectDescription: userData.projectDescription,
              fundingGoal: userData.fundingGoal,
              sector: userData.sector,
              teamSize: userData.teamSize,
              fundingTimeline: userData.fundingTimeline,
              projectFiles: userData.projectFiles,
              projectUrls: userData.projectUrls,
              website: userData.website,
              projectPurpose: userData.projectPurpose,
            });

            // Determine user type based on role
            let determinedUserType: UserType | "admin" = "investor";
            if (userData.role === "project_holder") {
              determinedUserType = "project-holder";
            } else if (userData.role === "admin") {
              determinedUserType = "admin";
            }

            setUserType(determinedUserType);
          }
        })
        .catch(e => {
          // If fetching fails, clear storage
          localStorage.removeItem("userId");
          localStorage.removeItem("userType");
          localStorage.removeItem("isAdmin");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, type: UserType | "admin"): Promise<boolean> => {
    try {
      // For all users (including admin), authenticate with Convex
      const userData = await loginMutation({ email, password });

      if (userData) {
        // Fetch full user data
        const fullUserData = await getUserMutation({ userId: userData.userId });

        if (fullUserData) {
          setUser({
            id: fullUserData.userId,
            name: fullUserData.name,
            email: fullUserData.email,
            role: fullUserData.role,
            status: fullUserData.status,
            phone: fullUserData.phone,
            company: fullUserData.company,
            bio: fullUserData.bio,
            location: fullUserData.location,
            investmentCapacity: fullUserData.investmentCapacity,
            preferredSectors: fullUserData.preferredSectors,
            // riskTolerance removed
            investmentExperience: fullUserData.investmentExperience,
            occupation: fullUserData.occupation,
            linkedin: fullUserData.linkedin,
            projectName: fullUserData.projectName,
            projectDescription: fullUserData.projectDescription,
            fundingGoal: fullUserData.fundingGoal,
            sector: fullUserData.sector,
            teamSize: fullUserData.teamSize,
            fundingTimeline: fullUserData.fundingTimeline,
            projectFiles: fullUserData.projectFiles,
            projectUrls: fullUserData.projectUrls,
            website: fullUserData.website,
            projectPurpose: fullUserData.projectPurpose,
          });

          // Determine user type based on role
          let determinedUserType: UserType | "admin" = "investor";
          if (fullUserData.role === "project_holder") {
            determinedUserType = "project-holder";
          } else if (fullUserData.role === "admin") {
            determinedUserType = "admin";
          }

          // Check if user is admin
          const isAdminUser = fullUserData.role === "admin";
          setIsAdmin(isAdminUser);

          setUserType(determinedUserType);
          localStorage.setItem("userId", fullUserData.userId);
          localStorage.setItem("userType", determinedUserType);
          localStorage.setItem("isAdmin", isAdminUser.toString());
          return true;
        }
      }

      return false;
    } catch (error: any) {
      console.error("Login error:", error);
      return false;
    }
  };

  const registerInvestor = async (data: { name: string; email: string; password: string; phone?: string; company?: string; bio?: string; location?: string; investmentCapacity?: number; preferredSectors?: string[]; investmentExperience?: string; occupation?: string; linkedin?: string; }): Promise<boolean> => {
    try {
      const userId = await registerInvestorMutation(data);
      return !!userId;
    } catch (error: any) {
      console.error("Investor registration error:", error);
      return false;
    }
  };

  const registerProjectHolder = async (data: { name: string; email: string; password: string; phone?: string; company?: string; bio?: string; location?: string; projectName?: string; projectDescription?: string; fundingGoal?: number; sector?: string; teamSize?: number; fundingTimeline?: string; projectFiles?: string[]; projectUrls?: string[]; website?: string; linkedin?: string; projectPurpose?: "sell" | "getInvestment"; assetsIncluded?: string[]; financialOverview?: string; }): Promise<boolean> => {
    try {
      const userId = await registerProjectHolderMutation(data);
      return !!userId;
    } catch (error: any) {
      console.error("Project holder registration error:", error);
      return false;
    }
  };
  const updateUser = async (data: Partial<AuthUser>): Promise<boolean> => {
    if (!user) return false;
    try {
      const { id, role, email, status, ...updateFields } = data;
      await updateUserMutation({
        userId: user.id,
        ...updateFields,
      });

      // Update local user state
      setUser(prev => prev ? { ...prev, ...updateFields } : null);
      return true;
    } catch (error: any) {
      console.error("Update user error:", error);
      return false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!user) return false;
    try {
      await changePasswordMutation({
        userId: user.id,
        currentPassword,
        newPassword,
      });
      return true;
    } catch (error: any) {
      console.error("Change password error:", error);
      return false;
    }
  };

  const registerAdmin = async (data: { name: string; email: string; password: string; }): Promise<boolean> => {
    try {
      const userId = await registerAdminMutation(data);
      return !!userId;
    } catch (error: any) {
      console.error("Admin registration error:", error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setUserType(null);
    setIsAdmin(false);
    localStorage.removeItem("userId");
    localStorage.removeItem("userType");
    localStorage.removeItem("isAdmin");
  };

  const value = {
    user,
    userType,
    isAdmin,
    login,
    registerInvestor,
    registerProjectHolder,
    registerAdmin,
    updateUser,
    changePassword,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};